import {
  Container,
  Box,
  Header,
  Button,
  Grid,
  SpaceBetween,
  Link,
  SegmentedControl,
  StatusIndicator,
  Icon,
  Spinner,
  Popover,
  ProgressBar,
} from '@cloudscape-design/components';

import { ViewLayout } from '../../components/ViewLayout';
import { useEffect, useMemo, useState } from 'react';
import {
  LoadedPlaceReport,
  PlaceReport,
  REPORT_CARD_TASKS,
  StatisticStatus,
  Task,
  TASK_NAME_INFO_MAP,
} from '../../common/types';
import { StatisticBox } from '../../components/StatisticBox';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { getReport } from '../../common/api/reports';
import { LoadingOrError } from '../../components/Loading';
import ReportCardHeader from '../../components/ReportCardHeader';
import { ReviewList } from '../../components/ReviewList';
import { TASK_NAME_MAP } from '../../common/types';
import { StringParam, useQueryParam, withDefault } from 'use-query-params';
import { KeywordList } from '../../components/KeywordList';
import { ScorecardHeader } from '../../components/ScorecardHeader';
import { ReportDownloadButton } from '../../components/ReportDownloadButton';
import { StarsIcon } from '../../components/StarsIcon';
import { CardTaskProgressView } from '../../components/CardTaskProgressView';

export default function View() {
  const { uuid } = useParams();

  const getQuery = useQuery({
    queryKey: ['reports', uuid],
    queryFn: () => getReport(uuid ?? ''),
    enabled: !!uuid,
    // 5 minutes
    staleTime: 1000 * 60 * 5,
    refetchOnWindowFocus: false,
  });

  return (
    <ViewLayout
      header={<ReportCardHeader user={getQuery.data?.user} />}
      contentType="dashboard"
      navigationHide
      headerVariant="high-contrast"
    >
      {getQuery.isLoading || !getQuery.data || getQuery.error ? (
        <LoadingOrError
          loading={getQuery.isLoading}
          retry={() => getQuery.refetch()}
          error={getQuery.error}
        />
      ) : (
        <CardTaskView report={getQuery.data} />
      )}
    </ViewLayout>
  );
}

export type CardTaskViewProps = {
  report: PlaceReport;
};

export function CardTaskView(props: CardTaskViewProps) {
  const [report, setReport] = useState(props.report);
  const getReq = useMutation({
    mutationFn: () => getReport(props.report.uuid),
    onSuccess: (data) => {
      // simple comparison to see if we need to update the session
      // TODO better comparison
      if (JSON.stringify(report) !== JSON.stringify(data)) {
        setReport(data);
      }
    },
  });

  const currentTask = useMemo(() => {
    if (report.tasks.length === 0) {
      return undefined;
    }
    return [...report.tasks]
      .filter((task) => REPORT_CARD_TASKS.includes(task.name))
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0];
  }, [report.tasks]);

  const status = useMemo(() => {
    if (report.periods && report.periods.length > 0) {
      return 'success';
    }
    if (!currentTask) {
      return 'in-progress';
    }
    if (currentTask.status === 'pending') {
      return 'in-progress';
    }
    if (currentTask.status === 'in-progress') {
      return 'in-progress';
    }
    // check for specific task name
    if (currentTask.status === 'completed') {
      if (currentTask.name === 'report_statistics') {
        return 'success';
      }
      return 'in-progress';
    }
    if (currentTask.status === 'failed' && currentTask.retries && currentTask.retries >= 3) {
      return 'error';
    }
    return 'in-progress';
  }, [currentTask]);

  useEffect(() => {
    const pollInterval = setInterval(async () => {
      // TODO call poll function better to look for changes
      if (status === 'in-progress') {
        await getReq.mutateAsync();
      }
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [getReq, report, status]);

  if (
    status === 'success' &&
    report.place !== undefined &&
    report.periods !== undefined &&
    report.periods.length > 0
  ) {
    return <CardView report={report as LoadedPlaceReport} />;
  }

  return <CardTaskProgressView place={report.place} currentTask={currentTask} status={status} />;
}

export type CardViewProps = {
  report: LoadedPlaceReport;
};

const RangeFiltersParam = withDefault(StringParam, '180-days');

const rangeIdToDays: Record<string, number> = {
  '30-days': 30,
  '60-days': 60,
  '90-days': 90,
  '180-days': 180,
};

export function CardView({ report }: CardViewProps) {
  const [selectedRange, setSelectedRange] = useQueryParam('range', RangeFiltersParam);

  // TODO make dynamic based on report.periods
  const rangeOptions = [
    { text: '30 Days', id: '30-days' },
    { text: '60 Days', id: '60-days' },
    { text: '90 Days', id: '90-days' },
    { text: '180 Days', id: '180-days' },
  ];

  const currentRange = rangeOptions.find((option) => option.id === selectedRange)!;
  const currentRangeIdx = useMemo(() => {
    return report.periods.findIndex((period) => period.range === selectedRange);
  }, [report.periods, selectedRange]);

  const placeName = report.place.name;
  const placeAddress = report.place.address;

  // TODO change to use report.report_at?
  const createdAt = new Date(report.created_at + 'Z');
  const rangeEnd = createdAt.toLocaleDateString();

  const period = report.periods[currentRangeIdx];
  const placeLetterGrade = period.place_letter_grade;
  const placePercentile = period.place_percentile;
  const reviewCount = period.review_count;
  const reviewCountStatus: StatisticStatus = period.review_count_status;
  const averageRating = period.average_rating;
  const averageRatingStatus: StatisticStatus = period.average_rating_status;
  const responseRate = period.response_rate;
  const responseRateStatus: StatisticStatus = period.response_rate_status;
  const avgResponseTime = period.avg_response_time;
  const avgResponseTimeStatus: StatisticStatus = period.avg_response_time_status;

  const rangeDays = rangeIdToDays[currentRange.id];

  const filteredReviews = useMemo(() => {
    // only show reviews from the current period
    return (
      report.reviews?.filter((review) => {
        return (
          review.review_date &&
          review.review_date >= period.start_date &&
          review.review_date <= period.end_date
        );
      }) ?? []
    );
  }, [report.reviews, period]);

  const borderColor = useMemo(() => {
    const grade = period.place_letter_grade;
    if (grade.includes('A')) {
      return 'border-green-600';
    } else if (grade.includes('B')) {
      return 'border-green-400';
    } else if (grade.includes('C')) {
      return 'border-yellow-400';
    }
    // D and F are both red
    return 'border-red-500';
  }, [period.place_letter_grade]);

  return (
    <Grid
      gridDefinition={[
        {
          colspan: {
            default: 12,
          },
        },
        {
          colspan: {
            default: 12,
          },
        },
        {
          colspan: {
            default: 12,
          },
        },
        {
          colspan: {
            default: 12,
          },
        },
      ]}
    >
      <Container
        header={
          <Header
            variant="h1"
            description={`Created ${rangeEnd}`}
            actions={
              <ReportDownloadButton item={report} period={currentRange.id} variant="button" />
            }
          >
            Reputation Scorecard
          </Header>
        }
        footer={
          <SegmentedControl
            selectedId={selectedRange}
            onChange={({ detail }) => setSelectedRange(detail.selectedId)}
            label="View past"
            options={rangeOptions}
          />
        }
      >
        <ScorecardHeader
          placeLetterGrade={placeLetterGrade}
          placePercentile={placePercentile}
          place={report.place}
          borderColor={borderColor}
        />
      </Container>
      <SpaceBetween direction="vertical" size="l">
        <Header variant="h2">Past {currentRange?.text ?? '30 Days'}</Header>
        <Grid
          gridDefinition={[
            {
              colspan: {
                default: 12,
                xs: 3,
              },
            },
            {
              colspan: {
                default: 12,
                xs: 3,
              },
            },
            {
              colspan: {
                default: 12,
                xs: 3,
              },
            },
            {
              colspan: {
                default: 12,
                xs: 3,
              },
            },
          ]}
        >
          {/* TODO make sure numbers are formatted correctly */}
          <StatisticBox
            statistic={reviewCount.toLocaleString()}
            percentile={period.review_count_percentile}
            status={reviewCountStatus}
            details={
              rangeDays !== 30
                ? `(${((reviewCount / rangeDays) * 30).toFixed(0)}/month)`
                : undefined
            }
            icon="contact"
            label="Reviews"
          />
          <StatisticBox
            statistic={averageRating.toFixed(2)}
            percentile={period.average_rating_percentile}
            status={averageRatingStatus}
            icon="star"
            label="Average Rating"
          />
          <StatisticBox
            statistic={(100 * responseRate).toFixed(0) + '%'}
            percentile={period.response_rate_percentile}
            status={responseRateStatus}
            icon="undo"
            label="Your Response Rate"
          />
          <StatisticBox
            statistic={avgResponseTime}
            percentile={period.avg_response_time_percentile}
            status={avgResponseTimeStatus}
            icon="calendar"
            label="Avg Response Time"
          />
        </Grid>
      </SpaceBetween>
      {period.keywords && period.keywords.length > 0 && (
        <KeywordList
          header={
            <Header
              variant="h2"
              description={`Frequently used keywords from reviews in the past ${currentRange?.text.toLowerCase() ?? '30 days'}`}
            >
              Frequently Used Keywords
            </Header>
          }
          keywords={period.keywords ?? []}
          range={currentRange?.text.toLowerCase() ?? '30 days'}
        />
      )}
      {filteredReviews && filteredReviews.length > 0 && (
        <ReviewList
          reviews={filteredReviews}
          placeName={placeName}
          header={
            <Header
              variant="h2"
              description={`Negative reviews and their replies in the past ${currentRange?.text.toLowerCase() ?? '30 days'}`}
            >
              Negative Reviews
            </Header>
          }
        />
      )}
    </Grid>
  );
}
